import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { getIsCustomMonthEventHighlighted } from "@common/reducers/CalendarSelectors";
import ContactAvatar from "@extension/context/content/components/bulk-dashboard/contact-table/contact-avatar";
import { Campaign as CampaignIcon } from "@mui/icons-material";
import { MoreVertical, Pause } from "lucide-react";
import { isStatusRecipient } from "@common/scheduling/status-recipient";
import moment from "moment";
import { memo, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { namespace } from "@common/config/constants";
import { Users } from "lucide-react";
import type { EventProps } from "react-big-calendar";
import { MessageAssetPreview } from "./message-asset-preview";
import { isPausedCampaignEvent } from "./paused-event";
import { getStatusConfig } from "@common-components/message-status/message-status-config";
import classes from "./calendar-view.module.scss";

// A paused campaign chip reads as INACTIVE via THREE independent cues (never color or
// opacity alone — accessible): the whole chip dims to 55%, the left accent drops its live
// color for a muted gray, and the campaign glyph becomes an explicit ⏸. Muted gray is a
// token with a neutral fallback so it stays theme-aware.
const PAUSED_ACCENT = 'var(--calendar-paused-accent, #9aa0a6)';
const PAUSED_ICON_BG = 'var(--calendar-paused-icon-bg, rgba(154, 160, 166, 0.18))';

interface Props {
  eventProps: EventProps<any>;
}

// Performance optimization: CustomMonthEvent now uses enriched event data
// directly from getCalendarLeanEvents instead of making per-event selector calls.
// This eliminates N+1 selector pattern where N events = N selector calls.

// DEBUG: Track event renders - disabled by default for production
const DEBUG_PERF = false;
let eventRenderCount = 0;
const PERF_PREFIX = '[CALENDAR_PERF]';

export const CustomMonthEvent = memo((props: Props) => {
  if (DEBUG_PERF) eventRenderCount++;
  const renderStart = DEBUG_PERF ? performance.now() : 0;

  const { t } = useTranslation(namespace);
  const { event } = props.eventProps;
  if (DEBUG_PERF) console.log(`${PERF_PREFIX} CustomMonthEvent render #${eventRenderCount}, id: ${event?._id?.slice(-6)}`);


  // Event data is now enriched in getCalendarLeanEvents - no need for per-event selector
  // The event object already contains: message, messageColor, asset, contactList, isPoll, poll

  // Check if the event is a Campaign or UserMessage
  const isEventCampaign = event.isCampaign;
  const isPaused = isPausedCampaignEvent(event);

  // Get highlight state (only applicable for UserMessages with contacts)
  const contactId = isEventCampaign ? null : event.contactList?.[0]?.id;
  const isHighlighted = useAppSelector(state =>
    contactId ? getIsCustomMonthEventHighlighted(state, contactId) : false
  );

  // Memoize computed values to prevent recalculation on parent re-renders
  const timeToDisplay = useMemo(() => moment(event.dueDate).format("HH:mm"), [event.dueDate]);

  // Get color for the event indicator (already computed in selector)
  const eventColor = event.messageColor ?? 'grey';

  // FE#335 — a scheduled WhatsApp Status ("Story"): distinct glyph + label so it
  // reads differently from a chat/group send on the calendar.
  const isStatusEvent = !isEventCampaign && isStatusRecipient(event.contactList?.[0]);

  const eventMessage = event.isPoll
    ? event.poll?.question
    : isStatusEvent
      ? `Status${event.message ? `: ${event.message}` : ''}`
      : event.message;

  const asset = event.asset;

  // FE#332 — reveal WHICH groups a campaign/drip event targets, without crowding
  // the space-tight cell. Names are pre-resolved in getCalendarLeanEvents. On
  // hover we show a small VISIBLE card (portal'd to body so the calendar cell's
  // overflow can't clip it); positioned from the chip's rect.
  const groupNames: string[] = event.audienceGroupNames ?? [];
  const groupCount: number = event.audienceGroupCount ?? 0;
  const hiddenGroupCount = Math.max(0, groupCount - groupNames.length);
  const hasGroupTip = isEventCampaign && groupNames.length > 0;
  const [tipPos, setTipPos] = useState<{ left: number; top: number } | null>(null);
  const onEnter = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!hasGroupTip) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTipPos({ left: r.left, top: r.bottom + 4 });
  }, [hasGroupTip]);
  const onLeave = useCallback(() => setTipPos(null), []);
  // The paused state keeps a native title as a lightweight always-available hint.
  const eventTitle = isPaused ? t('calendar.pausedCampaign', 'Paused campaign') : undefined;

  // FE#338 — a PAST occurrence (already ran) carries an effectiveStatus that has
  // left 'pending' (sent → unconfirmed/confirmed, failed, or missed → expired).
  // Show its status icon and dim the row so it reads as "done" while staying
  // visible; an upcoming 'pending' row is unchanged (no badge, full opacity).
  const effectiveStatus = isEventCampaign ? undefined : event.effectiveStatus;
  // §2/§3 — 'rate_limited' is an UPCOMING throttled send (still pending, waiting on the WhatsApp
  // backoff to lift), NOT a past occurrence: show its amber badge but keep it at full opacity and
  // out of the dim-as-done treatment. Past occurrences (sent/failed/expired) still dim.
  const isRateLimited = effectiveStatus === 'rate_limited';
  // #480 — 'in_transit' is likewise still upcoming (pending + a waMessageKey, ack outstanding):
  // show its badge at full opacity so it does not read as "scheduled" nor as already-run.
  const isInTransit = effectiveStatus === 'in_transit';
  const isPastOccurrence = !!effectiveStatus && effectiveStatus !== 'pending' && !isRateLimited && !isInTransit;
  const statusConfig = (isPastOccurrence || isRateLimited || isInTransit) ? getStatusConfig(effectiveStatus) : null;
  const StatusIcon = statusConfig?.icon;

  // DEBUG: Log render time
  if (DEBUG_PERF) console.log(`${PERF_PREFIX} CustomMonthEvent #${eventRenderCount} setup: ${(performance.now() - renderStart).toFixed(2)}ms`);

  return (
    <div
      className={`${classes.monthEvent} ${isHighlighted ? classes.highlighted : ''} calendar-event-container`}
      title={eventTitle}
      aria-label={hasGroupTip ? `${t('calendar.targetGroups', 'Groups')}: ${groupNames.join(', ')}` : (isPaused ? t('calendar.pausedCampaign', 'Paused campaign') : undefined)}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{
        display: 'flex',
        width: '100%',
        // Cue 1 (opacity) + Cue 2 (muted-gray left accent, drops the live color) — see PAUSED_ACCENT.
        // A paused chip dims to 55%; FE#338 also dims a PAST occurrence to 72% so it reads as
        // already-run while staying legible. Paused takes precedence when a chip is both.
        borderInlineStart: `4px solid ${isPaused ? PAUSED_ACCENT : eventColor}`,
        opacity: isPaused ? 0.55 : (isPastOccurrence ? 0.72 : 1),
      }}
    >
      <div style={{ width: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginInlineStart: '6px' }}>
        {isEventCampaign && (
          <div
            style={{
              width: '25px',
              height: '25px',
              borderRadius: '50%',
              // Cue 3: a paused campaign's glyph becomes an explicit ⏸ in a muted circle,
              // so the state is legible without relying on color or opacity.
              background: isPaused ? PAUSED_ICON_BG : 'var(--calendar-campaign-icon-bg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
            }}
          >
            {isPaused ? (
              <Pause size={14} strokeWidth={2.5} fill={PAUSED_ACCENT} color={PAUSED_ACCENT} aria-hidden="true" />
            ) : (
              <CampaignIcon
                style={{
                  color: 'var(--calendar-color-sage)',
                  fontSize: '16px',
                  lineHeight: 1,
                }}
              />
            )}
          </div>
        )}
        {!isEventCampaign && isStatusEvent && (
          <div
            style={{
              width: '25px',
              height: '25px',
              borderRadius: '50%',
              background: 'var(--calendar-status-icon-bg, #25D366)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
            }}
            title="WhatsApp Status"
            aria-label="WhatsApp Status"
          >
            <span style={{ fontSize: '13px', lineHeight: 1 }} role="img" aria-hidden>📢</span>
          </div>
        )}
        {!isEventCampaign && !isStatusEvent && (
          <ContactAvatar
            contactId={event.contactList?.[0]?.id}
            size={25}
            sx={{
              m: '0px',
            }}
            avatarStyle={{
              width: '25px',
              height: '25px',
              boxSizing: 'border-box',
            }}
            profilePicURL={event.contactList?.[0]?.profilePicUrl}
          />
        )}
      </div>

      <div style={{ flexGrow: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{timeToDisplay + ' - ' + eventMessage}</div>

      {asset?.url && asset?.type && (
        <MessageAssetPreview url={asset.url} type={asset.type} />
      )}

      {/* FE#338 — status badge for a past occurrence (sent ✓ / failed / missed).
          Colored by the shared status config so it matches the sidebar/detail view. */}
      {statusConfig && StatusIcon && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginInlineEnd: '22px',
            flexShrink: 0,
          }}
        >
          <StatusIcon size={14} style={{ color: statusConfig.color }} />
        </div>
      )}

      {/* Menu icon for all events (campaigns and messages) */}
      <div
        className="calendar-menu-button"
        style={{
          position: 'absolute',
          insetInlineEnd: '6px',
          top: '16px',
          transform: 'translateY(-50%)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          zIndex: 10,
          background: 'inherit',
          borderRadius: '4px',
          padding: '2px',
        }}
        title="Open menu"
      >
        <MoreVertical size={16} />
      </div>

      {/* FE#332 — visible hover-card listing the target group NAMES. Portal'd to
          <body> so the calendar cell's overflow can't clip it. */}
      {hasGroupTip && tipPos && createPortal(
        <div
          role="tooltip"
          style={{
            position: 'fixed', left: tipPos.left, top: tipPos.top, zIndex: 2147483000,
            maxWidth: 260, pointerEvents: 'none',
            background: 'var(--tooltip-bg, #1f2430)', color: 'var(--tooltip-fg, #fff)',
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.28)', padding: '8px 10px', fontSize: 12, lineHeight: 1.45,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, marginBottom: 4, opacity: 0.85 }}>
            <Users size={12} /> {t('calendar.targetGroups', 'Groups')}
          </div>
          {groupNames.map((name, i) => (
            <div key={i} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
          ))}
          {hiddenGroupCount > 0 && (
            <div style={{ opacity: 0.7, marginTop: 2 }}>
              {t('calendar.moreGroups', '+{{count}} more', { count: hiddenGroupCount })}
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
});
